import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Attorney,
  usePatchAttorneyMutation,
  useSetAttorneyAddressMutation,
} from '@/services/api/attorneysService';

type Props = {
  firmId: string;
  attorney: Attorney;
  onUpdated?: () => void;
};

export const EditAttorneyDialog = ({ firmId, attorney, onUpdated }: Props) => {
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState(attorney.first_name);
  const [lastName, setLastName] = useState(attorney.last_name);
  const [email, setEmail] = useState(attorney.email);
  const [phone, setPhone] = useState(attorney.phone);
  const [barNumber, setBarNumber] = useState(attorney.bar_number || '');

  // Address fields
  const [addressLine1, setAddressLine1] = useState(
    attorney.address?.line1 || '',
  );
  const [addressLine2, setAddressLine2] = useState(
    attorney.address?.line2 || '',
  );
  const [addressCity, setAddressCity] = useState(attorney.address?.city || '');
  const [addressState, setAddressState] = useState(
    attorney.address?.state || '',
  );
  const [addressPostcode, setAddressPostcode] = useState(
    attorney.address?.postcode || '',
  );

  const [patchAttorney, { isLoading }] = usePatchAttorneyMutation();
  const [setAttorneyAddress, { isLoading: isAddressLoading }] =
    useSetAttorneyAddressMutation();

  useEffect(() => {
    if (!open) return;
    setFirstName(attorney.first_name);
    setLastName(attorney.last_name);
    setEmail(attorney.email);
    setPhone(attorney.phone);
    setBarNumber(attorney.bar_number || '');
    // Reset address fields
    setAddressLine1(attorney.address?.line1 || '');
    setAddressLine2(attorney.address?.line2 || '');
    setAddressCity(attorney.address?.city || '');
    setAddressState(attorney.address?.state || '');
    setAddressPostcode(attorney.address?.postcode || '');
  }, [open, attorney]);

  const handleSave = async () => {
    if (!firmId) {
      toast.error('Missing firm');
      return;
    }
    try {
      // Update attorney basic info
      await patchAttorney({
        firm_id: firmId,
        id: attorney.id,
        patch: {
          id: attorney.id,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          bar_number: barNumber || null,
        },
      }).unwrap();

      // Update address if any address field has a value
      const hasAddressData =
        addressLine1 ||
        addressLine2 ||
        addressCity ||
        addressState ||
        addressPostcode;
      if (hasAddressData) {
        await setAttorneyAddress({
          firm_id: firmId,
          id: attorney.id,
          body: {
            line1: addressLine1 || null,
            line2: addressLine2 || null,
            city: addressCity || null,
            state: addressState || null,
            postcode: addressPostcode || null,
            country: 'US',
          },
        }).unwrap();
      }

      toast.success('Attorney updated');
      setOpen(false);
      if (onUpdated) onUpdated();
    } catch (e: any) {
      const msg = e?.data?.detail || 'Failed to update attorney';
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Attorney</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label htmlFor="edit_atty_first_name">First Name</Label>
            <Input
              id="edit_atty_first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="edit_atty_last_name">Last Name</Label>
            <Input
              id="edit_atty_last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="edit_atty_email">Email</Label>
            <Input
              id="edit_atty_email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="edit_atty_phone">Phone</Label>
            <Input
              id="edit_atty_phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Field>
          <Field>
            <Label htmlFor="edit_atty_bar_number">Bar/Juris Number</Label>
            <Input
              id="edit_atty_bar_number"
              value={barNumber}
              onChange={(e) => setBarNumber(e.target.value)}
              placeholder="Optional"
            />
          </Field>

          {/* Address Section */}
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Address</h4>
            <div className="space-y-3">
              <Field>
                <Label htmlFor="edit_atty_address_line1">Address Line 1</Label>
                <Input
                  id="edit_atty_address_line1"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street address"
                />
              </Field>
              <Field>
                <Label htmlFor="edit_atty_address_line2">Address Line 2</Label>
                <Input
                  id="edit_atty_address_line2"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Suite, unit, etc. (optional)"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <Label htmlFor="edit_atty_address_city">City</Label>
                  <Input
                    id="edit_atty_address_city"
                    value={addressCity}
                    onChange={(e) => setAddressCity(e.target.value)}
                    placeholder="City"
                  />
                </Field>
                <Field>
                  <Label htmlFor="edit_atty_address_state">State</Label>
                  <Input
                    id="edit_atty_address_state"
                    value={addressState}
                    onChange={(e) => setAddressState(e.target.value)}
                    placeholder="State"
                  />
                </Field>
              </div>
              <Field>
                <Label htmlFor="edit_atty_address_postcode">Postal Code</Label>
                <Input
                  id="edit_atty_address_postcode"
                  value={addressPostcode}
                  onChange={(e) => setAddressPostcode(e.target.value)}
                  placeholder="ZIP / Postal code"
                />
              </Field>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={isLoading || isAddressLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isLoading ||
              isAddressLoading ||
              !firstName ||
              !lastName ||
              !email ||
              !phone
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
